import { ISelectOption } from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import {
  IClients,
  Level,
  IBrands,
  IGroups,
  ISites,
  IUserData,
  emailRegex,
  nameRegex,
  usernameRegex,
  Role,
} from '../entities';
import { getLevelText } from '../sections/new-user/utils/helpers';
import { getIconFromLevel, getIconColorFromLevel } from 'sections/client-management/utils/ClientHelpers';
import { IUser } from '../sections/new-user/data/NewUserConstants';
import { sortOptions } from 'core/ui/utils/sorting';

export const getClientId = (userData: IUserData) => {
  switch (userData.accessLevel) {
    case 'SaleCycle':
      return 'SaleCycle';
    case 'Group':
      return userData.groupIds[0];
    case 'Brand':
      return userData.brandIds[0];
    case 'Site':
      return userData.siteIds[0];
    case 'MultiSite':
      return userData.siteIds.join(', ');
    default:
      return '';
  }
};

export const getClientName = (userData: IUserData, clients: IClients, t: TFunction): string => {
  switch (userData?.accessLevel) {
    case 'SaleCycle':
      return t('userManagement.users.table.clients.all');
    case 'Group':
      const group = clients.groups.byId[userData.groupIds[0]];
      return group?.name ?? '???';
    case 'Brand':
      const brand = clients.brands.byId[userData.brandIds[0]];
      return brand?.name ?? '???';
    case 'Site':
      const site = clients.sites.byId[userData.siteIds[0]];
      return site?.name ?? '???';
    case 'MultiSite':
      return userData.siteIds.map(id => clients?.sites?.byId[id]?.name ?? '???').join(', ');
    default:
      return t('userManagement.users.table.clients.na');
  }
};

//NOTE: We aren't supporting GROUP yet but UI supports it fine - just need to re-add it
//here when Product Team want to support GROUP-level users.
export const getAccessLevelOptions = (t: TFunction): ISelectOption<string>[] => [
  {
    value: 'SaleCycle',
    label: getLevelText('SaleCycle', t),
    icon: { name: getIconFromLevel('SaleCycle'), color: getIconColorFromLevel('SaleCycle') },
  },
  {
    value: 'Brand',
    label: getLevelText('Brand', t),
    icon: { name: getIconFromLevel('Brand'), color: getIconColorFromLevel('Brand') },
  },
  {
    value: 'Site',
    label: getLevelText('Site', t),
    icon: { name: getIconFromLevel('Site'), color: getIconColorFromLevel('Site') },
  },
  {
    value: 'MultiSite',
    label: getLevelText('MultiSite', t),
    icon: { name: getIconFromLevel('MultiSite'), color: getIconColorFromLevel('MultiSite') },
  },
];

export const isAccessLevelSaleCycle = (value: Level): boolean => value === 'SaleCycle';

/**
 * @deprecated See getSelectOptionsForClientLevel for info on what to use
 * instead
 */
export function getBrandOptions(clients) {
  return getSelectOptionsForClientLevel('Brand', clients);
}

/**
 * @deprecated See getSelectOptionsForClientLevel for info on what to use
 * instead
 */
export function getSiteOptions(clients) {
  return getSelectOptionsForClientLevel('Site', clients);
}

/**
 * @deprecated See getSelectOptionsForClientLevel for info on what to use
 * instead
 */
export function getGroupOptions(clients) {
  return getSelectOptionsForClientLevel('Group', clients);
}

/**
 * @deprecated See getSelectOptionsForClientLevel for info on what to use
 * instead
 */
export function getMultiSiteOptions(clients, groupId) {
  return getSelectOptionsForClientLevel('MultiSite', clients, groupId);
}

export const getRoleOptions = (roles: Role[], accessLevelIsSaleCycle = false): ISelectOption<string>[] => {
  // The filter is a workaround to prevent the combination of SaleCycle access level with External Client role. - OFFSITE-1289
  return roles
    .filter((value: Role) => !accessLevelIsSaleCycle || value.name !== 'External Client')
    .map(role => ({
      value: role.id,
      label: role.name,
    }));
};

/**
 * @deprecated This should only be used for clients obtained from the legacy
 * redux store. Prefer the `useClients` hook instead wherever possible then you
 * can use `entityToSortedSelectOptions` or `getSelectOptionsForMultiSites`
 * instead of this function for select options.
 */
export const getSelectOptionsForClientLevel = (
  level: Level,
  clients: IClients,
  groupId?: string,
): ISelectOption<string>[] => {
  switch (level) {
    case 'Site':
      const sites = mapOptions(clients.sites);
      return sites.sort(sortOptions);
    case 'Brand':
      const brands = mapOptions(clients.brands);
      return brands.sort(sortOptions);
    case 'Group':
      const groups = mapOptions(clients.groups);
      return groups.sort(sortOptions);
    case 'MultiSite':
      if (groupId) {
        const groupSites = mapOptions(clients.sites).filter(siteOption => {
          const site = clients.sites.byId[siteOption.value];
          const brand = clients.brands.byId[site.brandId];
          return brand?.groupId === groupId;
        });
        return groupSites.sort(sortOptions);
      } else {
        return [];
      }
  }
};

const mapOptions = (options: IBrands | IGroups | ISites): ISelectOption<string>[] => {
  return options.allIds.map((id: string) => {
    return { value: id, label: options.byId[id].name };
  });
};

export const isAccessLevelValid = (user: IUser) => {
  let valid = false;
  if (user.selectedAccessLevel) {
    switch (user.selectedAccessLevel.value) {
      case 'SaleCycle': {
        valid = true;
        break;
      }
      case 'Brand': {
        valid = user?.selectedBrand != null;
        break;
      }
      case 'Site': {
        valid = user?.selectedSite != null;
        break;
      }
      case 'MultiSite': {
        valid = user?.selectedMultiSiteGroup != null && user?.selectedMultiSites?.length > 0;
        break;
      }
    }
  }
  return valid;
};

export const fullNameValidCheck = (value: string) => {
  const name = value?.trim();
  return name?.length >= 2 && name?.length <= 200 && nameRegex.test(name);
};

export const emailValidCheck = (value: string) => {
  const email = value?.trim();
  return email.length !== 0 && emailRegex.test(email);
};

export const usernameValidCheck = (value: string) => {
  const name = value?.trim();
  return name?.length >= 2 && name?.length <= 200 && usernameRegex.test(name);
};

export const formatClients = (clients: string, clientOptions: ISelectOption<string>[]) => {
  if (!clients) {
    return '';
  }
  const clientObjects = clients.split(',');
  const clientNames = clientObjects.map(s => clientOptions.find(o => o.value === s)?.label);
  return clientNames.join(', ');
};
